<template>
  <div>
    <div
      class="row g-3 mb-3"
      role="form"
      :aria-labelledby="ariaLabel"
    >
      <div class="col-md-6">
        <AppInput
          :name="prefix + 'PhysicianDetails.Title'"
          :label="$root.t('step3.salutations')"
        >
          <AppSelect
            v-model="formData.salutationId"
            :placeholder="$t('common.defaultSelectionValue')"
            :options="$optionData.titles"
          />
        </AppInput>
      </div>

      <div class="col-md-6">
        <AppInput
          :name="prefix + 'PhysicianDetails.Forename'"
          :label="$root.t('step1.forename')"
        >
          <Input
            v-model="formData.forename"
            class-name="form-control"
            auto-complete="nope"
          />
        </AppInput>
      </div>

      <div class="col-md-6">
        <AppInput
          :name="prefix + 'PhysicianDetails.Surname'"
          :label="$root.t('step1.surname')"
        >
          <Input
            v-model="formData.surname"
            class-name="form-control"
            auto-complete="nope"
          />
        </AppInput>
      </div>

      <div class="col-md-6">
        <AppInput
          :name="prefix + 'PhysicianDetails.PracticeName'"
          :label="$root.t('step3.practiceName')"
        >
          <Input
            v-model="formData.practiceName"
            class-name="form-control"
            auto-complete="nope"
          />
        </AppInput>
      </div>

      <div class="col-md-6">
        <AppInput
          :name="prefix + 'PhysicianDetails.DateLastVisited'"
          :label="$root.t('step3.dateLastVisited')"
          rules="dayBeforeToday"
        >
          <AppDatePicker v-model="formData.dateLastVisited" />
        </AppInput>
      </div>

      <div class="col-md-6">
        <AppInput
          :name="prefix + 'PhysicianDetails.Email'"
          :label="$root.t('step3.email')"
          rules="email"
        >
          <Input
            v-model="formData.email"
            class-name="form-control"
            auto-complete="nope"
          />
        </AppInput>
      </div>

      <div class="col-md-6">
        <AppInput
          :name="prefix + 'PhysicianDetails.CountryCodes'"
          :label="$root.t('step3.countryCode')"
        >
          <AppSelect
            v-model="formData.countryCodeId"
            :options="$optionData.countryCodes"
            :placeholder="$t('common.defaultSelectionValue')"
            searchable
          />
        </AppInput>
      </div>

      <div class="col-md-6">
        <AppInput
          :name="prefix + 'PhysicianDetails.TelephoneNumber'"
          :label="$root.t('step3.telNumber')"
          rules="phoneNumber"
        >
          <Input
            v-model="formData.telephoneNumber"
            class-name="form-control"
            auto-complete="nope"
          />
        </AppInput>
      </div>
    </div>

    <AddressForm
      :form-data="formData.address"
      :address1required="false"
      :country-required="false"
      :option-countries="$optionData.residenceCountries"
      prefix="PhysicianDetails.Address."
    />

    <div class="row g-3">
      <AppInputCheck
        v-model="formData.haveExistingHealthInsurance"
        :name="prefix + 'ExistingHealthInsurancePoliciesRadiolb'"
        :label="$root.t('step3.haveExistInsured')"
        rules="required"
        class="col-md-12"
        label-class="h5 fw-bold text-primary form-section__title mt-2"
      >
        <div
          v-if="prefix !== '' && $formData.policyDetails.policyHolderInsured && !print"
          class="form-check form-section__field"
        >
          <div>
            <input
              :id="prefix + 'PhysicianSameAsPolicyHolder'"
              class="form-check-input"
              type="checkbox"
              name="FamilyMember.ConfirmChecked"
              @change="clone()"
            >
            <label
              :for="prefix + 'PhysicianSameAsPolicyHolder'"
              class="form-check-label"
            >
              <span
                v-for="(item, index) in $root.t('common.samePolicy')"
                :key="index"
              >
                {{ item }} <br>
              </span>
            </label>
          </div>
        </div>

        <div role="radiogroup">
          <div class="form-check form-check-inline">
            <input
              :id="prefix + 'ExistingHealthInsurancePoliciesRadio-yes'"
              v-model="formData.haveExistingHealthInsurance"
              class="form-check-input"
              type="radio"
              :name="prefix + 'ExistingHealthInsurancePoliciesRadio-yes'"
              :value="true"
              :aria-labelledby="prefix + 'ExistingHealthInsurancePoliciesRadiolb'"
              @change="changeInsuranceProvider(true)"
            >
            <label
              class="form-check-label"
              :for="prefix + 'ExistingHealthInsurancePoliciesRadio-yes'"
            >{{ $t('common.yesAnswer') }}</label>
          </div>
          <div class="form-check form-check-inline">
            <input
              :id="prefix + 'ExistingHealthInsurancePoliciesRadio-no'"
              v-model="formData.haveExistingHealthInsurance"
              class="form-check-input"
              type="radio"
              :name="prefix + 'ExistingHealthInsurancePoliciesRadio-no'"
              :value="false"
              :aria-labelledby="prefix + 'ExistingHealthInsurancePoliciesRadiolb'"
              @change="changeInsuranceProvider(false)"
            >
            <label
              class="form-check-label"
              :for="prefix + 'ExistingHealthInsurancePoliciesRadio-no'"
            >{{ $t('common.noAnswer') }}</label>
          </div>
        </div>
      </AppInputCheck>

      <div
        v-if="formData.haveExistingHealthInsurance"
        class="form-field ps-3 mt-0"
      >
        <div class="p-3 border-start border-primary">
          <div class="form-group">
            <div class="form-section__field">
              <div class="col-md-12">
                <AppInput
                  :name="prefix + 'CoverEndDate'"
                  :label="$root.t('step3.haveExistInsuredDate')"
                  rules="required"
                >
                  <AppDatePicker
                    v-model="formData.insuranceProvider.coverEndDate"
                    :disable-date="preventDate"
                  />
                </AppInput>
              </div>
            </div>

            <div class="">
              <div class="col-md-12">
                <AppInput
                  :name="prefix + 'ProviderCompanyName'"
                  :label="$root.t('step3.haveExistInsuredCompanyName')"
                  rules="required"
                >
                  <Input
                    v-model="formData.insuranceProvider.providerCompanyName"
                    class-name="form-control"
                    auto-complete="nope"
                  />
                </AppInput>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import AppSelect from '../../shared/AppSelect.vue'
import AddressForm from '../Form/AddressForm.vue'
import moment from 'moment'
export default {
  name: 'PhysicalDetailsForm',
  components: {
    AppSelect,
    AddressForm,
  },
  props: {
    formData: { type: Object, required: true },
    prefix: { type: String, default: '' },
    print: {
      type: Boolean,
      default: false,
    },
    ariaLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      cloneHealthInsuranceQuestion: false,
    }
  },
  computed: {
    healthInsuraceSamePolicyHolder() {
      let result = this.formData.healthInsuraceSamePolicyHolder !== undefined
      return result
    },
  },
  mounted() {},
  methods: {
    changeInsuranceProvider(status) {
      if (status) {
        this.formData.insuranceProvider = {
          coverEndDate: null,
          providerCompanyName: '',
        }
      } else {
        this.formData.insuranceProvider = null
      }
    },

    clone() {
      if (this.cloneHealthInsuranceQuestion === false) {
        const policyHolderPhysicalDetails = this.$formData.policyHolder.additionalDetails.physicianDetails
        this.formData.haveExistingHealthInsurance = policyHolderPhysicalDetails.haveExistingHealthInsurance
        // Set value clone for family
        // If true => has answer
        if (policyHolderPhysicalDetails.haveExistingHealthInsurance) {
          this.formData.insuranceProvider = {
            coverEndDate: null,
            providerCompanyName: '',
          }
          Object.assign(this.formData.insuranceProvider, policyHolderPhysicalDetails.insuranceProvider)
        } else {
          // Else => answer value = null
          this.formData.insuranceProvider = null
        }

        this.cloneHealthInsuranceQuestion = true
      } else {
        this.cloneHealthInsuranceQuestion = false
        this.formData.haveExistingHealthInsurance = undefined
        this.formData.insuranceProvider = null
      }
    },

    preventDate(date) {
      let today = moment().startOf('day')
      return moment(date).startOf('day') < today
    },
  },
}
</script>
